import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Bell, X, UserPlus, RotateCcw, Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface ActionItem {
  id: string;
  priority: "high" | "medium" | "low";
  label: string;
  patientEmail?: string | null;
  type: "pending_followup" | "inactive" | "cancelled_winback" | "new_order";
  createdAt?: string;
}

interface ActionQueueProps {
  items: ActionItem[];
}

const priorityConfig = {
  high: { color: "destructive" as const, icon: AlertTriangle },
  medium: { color: "default" as const, icon: Bell },
  low: { color: "secondary" as const, icon: UserPlus },
};

const ActionQueue = ({ items }: ActionQueueProps) => {
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem("sales_dismissed_actions");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  const dismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    sessionStorage.setItem("sales_dismissed_actions", JSON.stringify([...next]));
  };

  const visible = items.filter((i) => !dismissed.has(i.id));
  const filtered = priorityFilter === "all" ? visible : visible.filter((i) => i.priority === priorityFilter);
  const sorted = [...filtered].sort((a, b) => {
    const priorityRank = { high: 0, medium: 1, low: 2 };
    const typeRank = { cancelled_winback: 0, pending_followup: 1, inactive: 2, new_order: 3 };
    const p = priorityRank[a.priority] - priorityRank[b.priority];
    if (p !== 0) return p;
    const t = typeRank[a.type] - typeRank[b.type];
    if (t !== 0) return t;
    if (a.createdAt && b.createdAt) return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return 0;
  });

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Bell size={16} /> Action Queue
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-8 w-[120px] text-xs">
                <Filter size={12} className="mr-1 shrink-0" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            {dismissed.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1 h-8"
                onClick={() => {
                  setDismissed(new Set());
                  sessionStorage.removeItem("sales_dismissed_actions");
                }}
              >
                <RotateCcw size={12} /> Reset ({dismissed.size})
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {priorityFilter !== "all"
              ? `No ${priorityFilter}-priority actions right now.`
              : "🎉 All caught up — no actions needed right now!"}
          </p>
        ) : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {sorted.map((item) => {
              const cfg = priorityConfig[item.priority];
              const Icon = cfg.icon;
              return (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border bg-secondary/30 hover:bg-secondary/50 transition-colors"
                >
                  <Icon size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{item.label}</p>
                    {item.patientEmail && (
                      <p className="text-xs text-muted-foreground truncate">{item.patientEmail}</p>
                    )}
                  </div>
                  <Badge variant={cfg.color} className="text-[10px] shrink-0">
                    {item.priority}
                  </Badge>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => dismiss(item.id)}>
                    <X size={12} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionQueue;
